import { Breadcrumb, type Crumb } from "@/components/ui/Breadcrumb";
import { Container, Section } from "@/components/ui/Container";
import { cn } from "@/components/ui/cn";

/**
 * The "ground" shell of the hero family (decisions.md D35): how a workspace
 * page opens — the library listings, news and posts indexes, documents, the
 * learner's own pages (enrolled, saved, the profile set). An eyebrow, the page
 * title with an optional count, an optional description, and the accent rule.
 * It was the same markup pasted into eleven files and four loading skeletons
 * (2026-09-14 review), which is how two line-heights and two rule gradients
 * came to exist for one heading.
 *
 * Three pieces, smallest first — take the one the page needs:
 *  - `AccentRule`: the hairline alone, for section-scale headings that set
 *    their own type (`profil/narocnine`, `JourneyOverview`);
 *  - `WorkspaceHeading`: the heading, for a page that owns its layout;
 *  - `WorkspaceHero`: the heading on the mesh band under a breadcrumb — the
 *    whole opening of a library index.
 * Each has a skeleton twin with the same geometry, so a loading file cannot
 * drift from the page it stands in for.
 *
 * No hooks and no "use client": server pages and the client listing both
 * import it.
 */

/** The accent hairline under a heading: accent at the left, fading out right. */
export function AccentRule({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn("h-px w-full", className)}
      style={{
        background:
          "linear-gradient(90deg, color-mix(in srgb, var(--color-accent) 55%, transparent), hsl(0 0% 0% / 0.06) 30%, hsl(0 0% 0% / 0.06) 70%, transparent)",
      }}
    />
  );
}

type HeadingProps = {
  /** The section the page belongs to ("Akademija · Razišči"). A page that IS
   *  its own section — my academy, the library — has none. */
  eyebrow?: string;
  title: string;
  /** How many things the page lists — rendered beside the title; 0 and
   *  undefined render nothing (an empty page says so in its body). */
  count?: number;
  description?: string;
};

export function WorkspaceHeading({ eyebrow, title, count, description }: HeadingProps) {
  return (
    <header>
      {eyebrow ? (
        <p className="font-display text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-foreground/45">
          {eyebrow}
        </p>
      ) : null}
      <div className={cn("flex items-baseline gap-3", eyebrow && "mt-1.5")}>
        {/* 1.05, not `leading-none`: a title that wraps on a phone keeps its
            descenders clear of the next line. */}
        <h1 className="!text-[34px] !font-extrabold !leading-[1.05] !tracking-tight !text-foreground md:!text-[42px]">
          {title}
        </h1>
        {count ? (
          <span className="font-display text-[15px] font-bold tabular-nums text-foreground/40">
            {count}
          </span>
        ) : null}
      </div>
      {description ? (
        <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-muted-foreground md:text-[15px]">
          {description}
        </p>
      ) : null}
      <AccentRule className="mt-5" />
    </header>
  );
}

/** Same box as `WorkspaceHeading`; the rule is the real one, so it does not
 *  pop in when the content arrives. */
export function WorkspaceHeadingSkeleton({ description = false }: { description?: boolean }) {
  return (
    <div>
      <div className="h-2.5 w-28 animate-pulse rounded bg-muted" />
      <div className="mt-2 h-[34px] w-64 max-w-[80%] animate-pulse rounded bg-muted md:h-[42px] md:w-80" />
      {description ? (
        <div className="mt-3 h-4 w-96 max-w-full animate-pulse rounded bg-muted" />
      ) : null}
      <AccentRule className="mt-5" />
    </div>
  );
}

const HERO_BAND = "app-hero-mesh relative overflow-hidden !pt-8 md:!pt-12 !pb-2";

export function WorkspaceHero({ crumbs, ...heading }: HeadingProps & { crumbs: Crumb[] }) {
  return (
    <Section className={HERO_BAND}>
      <Container>
        <Breadcrumb items={crumbs} />
        <div className="mt-4">
          <WorkspaceHeading {...heading} />
        </div>
      </Container>
    </Section>
  );
}

export function WorkspaceHeroSkeleton() {
  return (
    <Section className={HERO_BAND}>
      <Container>
        <div className="h-3.5 w-40 animate-pulse rounded bg-muted" />
        <div className="mt-4">
          <WorkspaceHeadingSkeleton />
        </div>
      </Container>
    </Section>
  );
}
